import type { LocationResponse } from '../api/generated';
import {
  LOCATION_COLOR_IDS,
  LOCATION_COLOR_PALETTE,
  getLocationColor,
  type LocationColorId
} from '../constants/locationColors';
import { hashStringToIndex } from '../utils/stringUtils';

// The two sides of the same colour: the UI works in palette token ids ('blue',
// 'rose'), the backend stores a location's colour as a 6-digit hex string. The
// translation happens here, at the API boundary, so no component ever has to
// know a hex value and no request ever carries a token name.

// Outgoing: the hex written to the backend on create and update.
export const getLocationColorHex = (colorId: LocationColorId): string =>
  getLocationColor(colorId).hex;

// Incoming lookup, keyed lowercase because the stored value's case is the
// backend's to choose — it only enforces the format, not the casing.
//
// A Map rather than a plain object: an object lookup would resolve inherited
// members, so a stored colour of "constructor" would come back as a truthy
// function instead of an unknown value.
const COLOR_ID_BY_HEX = new Map<string, LocationColorId>(
  LOCATION_COLOR_PALETTE.map((color) => [color.hex.toLowerCase(), color.id])
);

// Null rather than a default, so the caller decides what an unrecognised colour
// should look like (see resolveLocationColorId).
export const getLocationColorIdFromHex = (hex: string): LocationColorId | null =>
  COLOR_ID_BY_HEX.get(hex.trim().toLowerCase()) ?? null;

// A center that has never been given a colour still needs a stable identity, so
// its colour is derived from its name, the same way the header colour line's
// predecessor (locationLineColors.ts, removed with the locations settings work)
// always did.
//
// It is not the same palette though: the old derivation hashed across 3 colours
// while this hashes across all 6 location colours. So roughly half of existing
// centers get a default of green, amber or rose, which the header could not
// previously show. That is expected, not a regression. Hash into a 3-entry slice
// of LOCATION_COLOR_IDS if the defaults ever need to be invisible.
export const getDefaultLocationColorId = (locationName: string): LocationColorId =>
  LOCATION_COLOR_IDS[hashStringToIndex(locationName, LOCATION_COLOR_IDS.length)];

/**
 * The palette colour to show for a location as the backend returned it.
 *
 * Two cases fall back to the name-derived default rather than to a fixed
 * colour: a location with no stored colour (the field is optional — locations
 * created before it existed have none), and a stored hex outside the palette
 * (hand-written data, or a palette entry that has since changed value). The
 * name-derived default is the sensible fallback for both because it is stable
 * per location, so an unrecognised colour still gives that center one
 * consistent identity everywhere instead of collapsing every such center onto
 * the same swatch.
 */
export const resolveLocationColorId = (
  location: Pick<LocationResponse, 'name' | 'color'>
): LocationColorId => {
  const storedColorId = location.color ? getLocationColorIdFromHex(location.color) : null;
  return storedColorId ?? getDefaultLocationColorId(location.name);
};
